import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { s } from '@/theme/tokens';
import { InfoField } from '@/ui/composites/InfoField';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { LayoutGrid, LayoutGridItem } from '@/ui/layout/LayoutGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Chip,
  formatStatusLabel,
  Link,
  resolveStatusChipTone,
  Surface,
} from '@/ui/primitives';
import type { BuildDto, SuiteExecutionDto } from '@flowqa/shared';
import type { ReactElement, ReactNode } from 'react';

export type SuiteExecutionDetailsPanelsProps = {
  execution: SuiteExecutionDto;
  build: BuildDto | null;
};

export const SuiteExecutionDetailsPanels = ({
  execution,
  build,
}: SuiteExecutionDetailsPanelsProps): ReactElement => {
  const noDataLabel = 'No data';
  const noDataText = (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {noDataLabel}
    </Box>
  );
  const hasBuildPanel = Boolean(build?.id);
  const wrapValueSx = {
    display: 'inline',
    whiteSpace: 'normal',
    overflow: 'visible',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  } as const;
  const renderWrappedValue = (value: ReactNode, href: string | null): ReactNode =>
    href ? (
      <Link component={RouterLink} to={href} underline="hover" color="inherit" sx={wrapValueSx}>
        {value}
      </Link>
    ) : (
      <Box component="span" sx={wrapValueSx}>
        {value}
      </Box>
    );
  const timestampLabel = formatDate(execution.timestamp, { includeTime: true }) || noDataLabel;
  const durationLabel =
    execution.durationMs !== null && execution.durationMs !== undefined
      ? formatDuration(execution.durationMs)
      : noDataLabel;
  const suiteLabel =
    execution.suiteName ?? execution.name ?? execution.suiteSlug ?? execution.suiteId;
  const suiteHref = execution.suiteSlug
    ? `/suites/${execution.suiteSlug}`
    : execution.suiteId
      ? `/suites/${execution.suiteId}`
      : null;
  const projectLabel = execution.projectName ?? execution.projectSlug ?? execution.projectId;
  const runLabel =
    execution.runNumber !== null && execution.runNumber !== undefined
      ? `#${execution.runNumber}`
      : noDataLabel;
  const resultsFileLabel =
    execution.resultsFile?.fileName ??
    execution.resultsFileId ??
    execution.resultsFile?.id ??
    noDataLabel;
  const resultsFileHref = execution.resultsFileId
    ? `/results-files/${execution.resultsFileId}`
    : null;
  const executionRunHref = execution.runId ? `/execution-runs/${execution.runId}` : null;
  const buildStatusLabel = build?.status ? formatStatusLabel(build.status) : noDataLabel;
  const pipelineLabel = build?.pipelineName ?? build?.pipelineId ?? noDataLabel;
  const pipelineSlug = build?.pipelineName ? toPipelineSlug(build.pipelineName) : null;
  const pipelineSegment =
    pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (build?.pipelineId ?? null);
  const buildRunNumber = resolveBuildRunNumber(build?.buildKey);
  const pipelineHref = pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines';
  const buildHref =
    pipelineSegment && buildRunNumber !== null
      ? `/pipelines/${pipelineSegment}/runs/${buildRunNumber}`
      : pipelineHref;
  const buildLabel = build?.buildKey ?? build?.id ?? noDataLabel;

  return (
    <Section paddingY="md">
      <Surface padding="lg" border>
        <LayoutGrid gap="md">
          <LayoutGridItem
            xs={12}
            sm={hasBuildPanel ? 3 : 4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingRight: s('md'), width: '100%' }}>
              <InfoField
                label="Suite"
                value={renderWrappedValue(suiteLabel, suiteHref)}
                gap="none"
              />
              <InfoField label="Run" value={runLabel} gap="none" />
              <InfoField label="Project" value={projectLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem
            xs={12}
            sm={hasBuildPanel ? 3 : 4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingX: s('md'), width: '100%' }}>
              <InfoField label="Timestamp" value={timestampLabel} gap="none" />
              <InfoField label="Duration" value={durationLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem
            xs={12}
            sm={hasBuildPanel ? 3 : 4}
            sx={[
              { display: 'flex' },
              ...(hasBuildPanel ? [{ borderRight: 0.5, borderColor: 'divider' }] : []),
            ]}
          >
            <Stack gap="sm" sx={{ paddingX: s('md'), width: '100%' }}>
              <InfoField
                label="Execution Run"
                value={
                  executionRunHref ? (
                    <Link
                      component={RouterLink}
                      to={executionRunHref}
                      underline="hover"
                      color="inherit"
                    >
                      {execution.runId}
                    </Link>
                  ) : (
                    execution.runId
                  )
                }
                gap="none"
              />
              <InfoField
                label="Results"
                value={
                  resultsFileHref ? (
                    <Link
                      component={RouterLink}
                      to={resultsFileHref}
                      underline="hover"
                      color="inherit"
                    >
                      {resultsFileLabel}
                    </Link>
                  ) : (
                    resultsFileLabel
                  )
                }
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
          {hasBuildPanel ? (
            <LayoutGridItem xs={12} sm={3}>
              <Stack gap="sm" sx={{ paddingLeft: s('md') }}>
                <InfoField
                  label="Build"
                  value={
                    build?.id ? (
                      <Link component={RouterLink} to={buildHref} underline="hover" color="inherit">
                        {buildLabel}
                      </Link>
                    ) : (
                      buildLabel
                    )
                  }
                  gap="none"
                />
                <InfoField
                  label="Pipeline"
                  value={
                    pipelineSegment ? (
                      <Link
                        component={RouterLink}
                        to={pipelineHref}
                        underline="hover"
                        color="inherit"
                      >
                        {pipelineLabel}
                      </Link>
                    ) : (
                      pipelineLabel
                    )
                  }
                  gap="none"
                />
                <InfoField
                  label="Build Status"
                  value={
                    build?.status ? (
                      <Chip
                        label={buildStatusLabel}
                        tone={resolveStatusChipTone(build.status)}
                        size="xs"
                        transform="uppercase"
                      />
                    ) : (
                      noDataText
                    )
                  }
                  gap="none"
                />
              </Stack>
            </LayoutGridItem>
          ) : null}
        </LayoutGrid>
      </Surface>
    </Section>
  );
};
